/**
 * DAC profiles -- group 03 (European and Japanese high-end converters).
 *
 * The brands in this group publish very little measured data: several of them
 * quote no THD figure at all, and none of them name the converter part they
 * use. Where a number is genuinely published it is used as-is; where it is not,
 * the entry carries 'ESTIMATED' and the provenanceNote says exactly which
 * quantities were inferred. What differentiates these profiles is therefore the
 * *architecture* -- conversion topology, output stage device physics, output
 * impedance, filter policy and clock quality -- not a distortion number copied
 * from a brochure.
 */

import type { DacProfile } from '../../types/hardware';
import {
  AKM_FILTERS,
  FILTERS,
  HARMONIC_SHAPES,
  SIMPLE_FILTERS,
  TUBE_LINE,
  frCurve,
  makeDac,
  usd,
} from '../kit';

export const DACS_03: DacProfile[] = [
  makeDac({
    mfr: 'Berkeley Audio Design',
    model: 'Alpha DAC Reference Series 3',
    price: usd(
      19500,
      'APPROXIMATE',
      2019,
      'Approximate US retail. Berkeley sells through a small dealer network and does not maintain a public price list, so this figure places the product rather than quoting a confirmed MSRP.',
    ),
    year: 2018,
    country: 'United States',
    architecture: 'multibit-delta-sigma',
    core: 'Custom digital filter engine feeding a multibit delta-sigma modulator, balanced dual-mono',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 124,
    thdPlusNDb: -114,
    crosstalkDb: -128,
    outputVrmsSE: 2.75,
    outputVrmsBal: 5.5,
    outputImpedanceOhms: 25,
    balanced: true,
    dsd: 'none',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    filters: [
      {
        ...FILTERS.sharpLinear(512),
        id: 'bad-fixed-fir',
        label: 'Fixed long-tap linear-phase reconstruction',
        stopbandDb: -140,
        cutoffRatio: 0.92,
        transitionRatio: 0.07,
      },
    ],
    jitterPsRms: 12,
    noiseShaperOrder: 5,
    confidence: 'ESTIMATED',
    accent: '#6f9bd1',
    tags: ['pcm-focused', 'fixed-filter', 'aes-input', 'hdcd-lineage'],
    notes:
      'The engineering that distinguishes this design is in the digital domain, not the analog one: a long fixed reconstruction filter and very careful wordlength handling ahead of the modulator, with no user-selectable roll-off options at all. Modelled with a single 512-tap linear-phase filter, a deep stopband and correspondingly symmetrical pre and post ringing, plus a low-jitter clock. The output stage is a discrete balanced Class A design, so what little distortion remains is low-order and rises smoothly with level rather than sitting on a fixed high-order floor.',
    provenanceNote:
      'Architecture family, PCM-only input policy and the fixed-filter design philosophy are taken from Berkeley published material. Dynamic range, THD+N, channel separation, output impedance and jitter are ESTIMATED from the product class: Berkeley does not publish a full specification sheet for this model. The converter part number is not disclosed by the manufacturer and is not claimed here.',
  }),

  makeDac({
    mfr: 'T+A elektroakustik',
    model: 'SDV 3100 HV',
    price: usd(
      34900,
      'APPROXIMATE',
      2021,
      'Approximate US retail for the HV-series streaming D/A converter; T+A pricing varies by market and configuration.',
    ),
    year: 2020,
    country: 'Germany',
    architecture: 'hybrid',
    core: 'Dual-path converter: quadruple true 1-bit converter for DSD with analog reconstruction, plus a double-differential quadruple multibit delta-sigma path for PCM',
    outputStage: 'jfet',
    dynamicRangeDb: 122,
    thdPlusNDb: -108,
    crosstalkDb: -118,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 22,
    balanced: true,
    dsd: 'dsd1024',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000, 705600, 768000],
    filters: [
      { ...FILTERS.sharpLinear(128), id: 'fir-long', label: 'FIR long (linear phase)' },
      { ...FILTERS.slowMinimum(32), id: 'fir-short', label: 'FIR short (minimum phase)' },
      { ...FILTERS.hybrid(64), id: 'bezier-iir', label: 'Bezier / IIR hybrid' },
      FILTERS.nos(),
    ],
    defaultFilterId: 'fir-long',
    jitterPsRms: 18,
    noiseShaperOrder: 4,
    slewRateVus: 80,
    confidence: 'ESTIMATED',
    accent: '#b8c4cc',
    tags: ['dual-converter', 'true-1-bit', 'high-voltage-stage', 'selectable-filters'],
    notes:
      'Two physically separate converters share one chassis: PCM goes through an oversampling multibit delta-sigma path with four selectable reconstruction filters including a genuine non-oversampling setting, while DSD is handled by a 1-bit converter that uses no digital filter at all and is band-limited by an analog network. The audible consequence modelled here is that reconstruction character is a user choice on the PCM path and fixed on the DSD path. The analog stage runs at a high supply voltage, which is modelled as a large linear region and a high slew rate, so compression on transients happens far above normal listening level.',
    provenanceNote:
      'Dual-converter architecture, the four-filter PCM menu and DSD1024 capability are from T+A published material. Dynamic range, THD+N, output impedance, slew rate and jitter are ESTIMATED from the product class; T+A does not publish a THD+N figure for this model in a form that can be quoted here.',
  }),

  makeDac({
    mfr: 'Burmester',
    model: '113',
    price: usd(
      4500,
      'APPROXIMATE',
      2018,
      'Approximate US retail. Burmester quotes in EUR and does not publish a stable US price list, so treat this as an approximate placement.',
    ),
    year: 2015,
    country: 'Germany',
    architecture: 'delta-sigma',
    core: 'Delta-sigma converter in a fully symmetrical, DC-coupled dual-mono layout',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 116,
    thdPlusNDb: -102,
    crosstalkDb: -105,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 50,
    balanced: true,
    dsd: 'none',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    filters: SIMPLE_FILTERS(),
    jitterPsRms: 40,
    noiseShaperOrder: 3,
    confidence: 'ESTIMATED',
    accent: '#d8d2c4',
    tags: ['dc-coupled', 'symmetrical', 'x-amp', 'compact'],
    notes:
      'A compact converter built around the house discrete gain module rather than an integrated op-amp, DC coupled throughout so there is no output coupling capacitor and therefore no low-frequency phase rotation above 2 Hz. Modelled with a moderate-feedback discrete Class A residual: second harmonic leads, the tail falls away quickly, and the absolute distortion floor sits roughly 10 dB above the flagship entries in this group. Clocking is the weak point of the compact format, so jitter is set higher than the large-chassis designs here, which broadens the skirts around high-frequency test tones.',
    provenanceNote:
      'Fully symmetrical DC-coupled topology and the discrete output module are from Burmester published descriptions. Dynamic range, THD+N, channel separation, output impedance and jitter are ESTIMATED from the product class; Burmester does not publish distortion figures. The DSD entry reflects a PCM-only input set and is inferred, not quoted.',
  }),

  makeDac({
    mfr: 'MBL',
    model: 'N31 CD-DAC',
    price: usd(
      15400,
      'APPROXIMATE',
      2019,
      'Approximate US retail for the Noble-line CD player and D/A converter.',
    ),
    year: 2016,
    country: 'Germany',
    architecture: 'delta-sigma',
    core: 'Balanced delta-sigma conversion with proprietary upsampling ahead of the modulator',
    outputStage: 'op-amp',
    dynamicRangeDb: 120,
    thdPlusNDb: -106,
    crosstalkDb: -110,
    outputVrmsSE: 2.2,
    outputVrmsBal: 4.4,
    outputImpedanceOhms: 60,
    balanced: true,
    dsd: 'dsd128',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: [FILTERS.sharpLinear(128), FILTERS.apodizing(128)],
    jitterPsRms: 30,
    noiseShaperOrder: 5,
    idleToneLevel: 0.12,
    confidence: 'ESTIMATED',
    accent: '#c9a227',
    tags: ['cd-transport', 'upsampling', 'balanced'],
    notes:
      'A disc transport and converter in one chassis, so the profile models the converter fed from an internal reclocked source rather than an asynchronous USB link. The distinguishing element is the integrated-op-amp current-to-voltage and buffer stage: its distortion floor is lower than the discrete Class A designs here at small signal but its residual is flatter across harmonic order, and a modest idle-tone tendency is included because the modulator runs at a lower order than the flagship entries. An apodizing filter is offered alongside the default sharp linear-phase filter, which trades stopband depth for the removal of pre-ringing.',
    provenanceNote:
      'Product class, balanced topology and disc-plus-converter configuration are from MBL published material. Dynamic range, THD+N, channel separation, output impedance, jitter, modulator order and idle-tone level are ESTIMATED; MBL does not publish these figures and does not disclose the converter part number. The output-stage classification is inferred from the product class and is not a manufacturer statement.',
  }),

  makeDac({
    mfr: 'Soulution',
    model: '560',
    price: usd(
      35000,
      'APPROXIMATE',
      2020,
      'Approximate US retail; Soulution pricing varies with configuration and exchange rate.',
    ),
    year: 2015,
    country: 'Switzerland',
    architecture: 'delta-sigma',
    core: 'Balanced delta-sigma conversion with FPGA upsampling into an ultra-wide-bandwidth voltage output stage',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 123,
    thdPlusNDb: -112,
    crosstalkDb: -130,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 2,
    balanced: true,
    dsd: 'dsd256',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: SIMPLE_FILTERS(),
    jitterPsRms: 10,
    noiseShaperOrder: 6,
    slewRateVus: 500,
    response: frCurve(
      [[10, 0], [20, 0], [1000, 0], [20000, 0], [40000, -0.05], [100000, -0.4], [200000, -1.6]],
      'ESTIMATED',
      'Wide-bandwidth output stage: first audible-band deviation is far above the audio range',
    ),
    confidence: 'ESTIMATED',
    accent: '#8e9aa6',
    tags: ['wide-bandwidth', 'low-output-impedance', 'fast-feedback'],
    notes:
      'The design premise is speed rather than filtering: a very fast feedback loop around the analog stage, a 2 ohm source impedance and a response that is still within a tenth of a dB at 40 kHz. Modelled with an essentially ruler-flat audio band, an extremely high slew rate so no realistic transient is rate-limited, and a distortion residual that is low in absolute terms and does not rise near full scale. Against the tube and discrete entries in this group its signature is the absence of one: no output-impedance interaction, no band-edge droop, no level-dependent harmonic growth.',
    provenanceNote:
      'Wide-bandwidth, low-output-impedance design philosophy is from Soulution published material. Dynamic range, THD+N, channel separation, slew rate, jitter and the exact response shape are ESTIMATED from the stated design goals; Soulution does not publish a full specification sheet for this model, and the converter part number is not disclosed.',
  }),

  makeDac({
    mfr: 'CH Precision',
    model: 'C1.2',
    price: usd(
      38000,
      'APPROXIMATE',
      2021,
      'Approximate US retail for a base single-chassis configuration; input boards, dual-mono operation and the external power supply add substantially.',
    ),
    year: 2020,
    country: 'Switzerland',
    architecture: 'fpga-custom',
    core: 'FPGA-based interpolation and conversion engine with user-selectable interpolation filters and variable oversampling',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 125,
    thdPlusNDb: -115,
    crosstalkDb: -132,
    outputVrmsSE: 3.0,
    outputVrmsBal: 6.0,
    outputImpedanceOhms: 15,
    balanced: true,
    dsd: 'dsd512',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000, 705600, 768000],
    filters: [
      { ...FILTERS.sharpLinear(256), id: 'filter-1', label: 'Filter 1: sharp, linear phase' },
      { ...FILTERS.sharpMinimum(192), id: 'filter-2', label: 'Filter 2: sharp, minimum phase' },
      { ...FILTERS.apodizing(160), id: 'filter-3', label: 'Filter 3: apodizing' },
      { ...FILTERS.slowLinear(48), id: 'filter-4', label: 'Filter 4: slow, linear phase' },
      { ...FILTERS.slowMinimum(32), id: 'filter-5', label: 'Filter 5: slow, minimum phase' },
    ],
    defaultFilterId: 'filter-1',
    jitterPsRms: 6,
    internalOversampling: 16,
    parallelUnits: 8,
    confidence: 'ESTIMATED',
    accent: '#a7b6c2',
    tags: ['fpga', 'modular', 'selectable-filters', 'external-clock'],
    notes:
      'Conversion is done in an FPGA rather than a fixed-function chip, with the interpolation stage exposed as a five-position user choice and the oversampling ratio itself adjustable. The profile therefore carries the widest filter menu in this group, spanning linear and minimum phase at two roll-off rates plus an apodizing option, so the same recording can be given pre-ringing, post-ringing or neither. Eight parallel conversion channels are summed per output, which lowers the uncorrelated noise floor by roughly 9 dB relative to a single channel; combined with a clock intended for external synchronisation, this is the lowest-jitter and lowest-noise entry in the group.',
    provenanceNote:
      'FPGA-based conversion, user-selectable interpolation filters, variable oversampling, parallel conversion channels and external clock synchronisation are from CH Precision published material. Dynamic range, THD+N, channel separation, output impedance and jitter are ESTIMATED from the product class; CH Precision does not publish these as simple single figures.',
  }),

  makeDac({
    mfr: 'Nagra',
    model: 'HD DAC',
    price: usd(
      24500,
      'APPROXIMATE',
      2014,
      'Approximate original US retail for the converter alone, without the optional outboard power supply.',
    ),
    year: 2014,
    country: 'Switzerland',
    architecture: 'ladder-multibit',
    core: 'Multibit ladder conversion stage feeding a single-ended Class A triode output through Nagra-wound output transformers',
    outputStage: 'tube',
    dynamicRangeDb: 110,
    thdPlusNDb: -84,
    crosstalkDb: -90,
    outputVrmsSE: 2.0,
    outputVrmsBal: 4.0,
    outputImpedanceOhms: 50,
    balanced: true,
    dsd: 'dsd64',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    filters: [
      { ...FILTERS.sharpLinear(96), id: 'fixed-os', label: 'Fixed oversampling FIR' },
      { ...FILTERS.nosCompensated(), label: 'Non-oversampling comparison mode (modelled)' },
    ],
    defaultFilterId: 'fixed-os',
    ladderMismatchLsb: 0.3,
    effectiveBits: 18,
    jitterPsRms: 35,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(5, -0.7),
    slewRateVus: 20,
    confidence: 'ESTIMATED',
    accent: '#e0a05c',
    tags: ['tube', 'transformer-coupled', 'ladder', 'studio-lineage'],
    notes:
      'A triode output stage, but transformer coupled rather than capacitor coupled, which is the point of the entry: the transformer sets a 50 ohm source impedance instead of the 600 to 1200 ohms a cathode-follower tube output would present, so it drives a following input stage without the usual high-frequency loss into cable capacitance. The transfer curve is strongly second-harmonic biased and its distortion grows about 1 dB per dB of level, and ladder element mismatch adds a level-dependent nonlinearity that does not vanish at low signal the way a delta-sigma residual does. The transformer also imposes the low-frequency corner modelled here, around 5 Hz.',
    provenanceNote:
      'Tube output stage and transformer-coupled output are from Nagra published material. The multibit ladder classification of the conversion stage is INFERRED from the manufacturer description and no converter part number is claimed. Dynamic range, THD+N, output impedance, ladder mismatch and jitter are ESTIMATED; Nagra does not publish THD for this product. The second filter entry is a modelled non-oversampling comparison mode for the simulator, not a documented front-panel option.',
  }),

  makeDac({
    mfr: 'Aurender',
    model: 'A30',
    price: usd(
      17999,
      'APPROXIMATE',
      2017,
      'Approximate US retail at launch for the caching server, CD ripper and converter in one chassis; now discontinued.',
    ),
    year: 2017,
    country: 'South Korea',
    architecture: 'multibit-delta-sigma',
    core: 'AKM AK4490EQ switched-resistor converter, dual-mono, fed from an internal solid-state cache',
    outputStage: 'op-amp',
    dynamicRangeDb: 118,
    thdPlusNDb: -108,
    crosstalkDb: -112,
    outputVrmsSE: 2.1,
    outputVrmsBal: 4.2,
    outputImpedanceOhms: 47,
    balanced: true,
    dsd: 'dsd256',
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000],
    filters: AKM_FILTERS(),
    jitterPsRms: 25,
    noiseShaperOrder: 5,
    idleToneLevel: 0.08,
    confidence: 'ESTIMATED',
    accent: '#7a8fa6',
    tags: ['server-dac', 'akm', 'selectable-filters', 'cached-playback'],
    notes:
      'A server and converter in one box, so playback is fed from a local cache rather than a network stream and the input-side jitter mechanism that dominates USB-fed converters largely disappears. The converter itself uses a switched-resistor output rather than a pure 1-bit modulator, which is why it is classified as multibit delta-sigma here: its low-level linearity behaves more like a segmented design than a high-order single-bit one. The filter menu is the datasheet set, including a super-slow roll-off option whose stopband is only about 32 dB down, so out-of-band images are deliberately left audible to the rest of the chain.',
    provenanceNote:
      'Converter family, dual-mono layout, cached playback and the filter menu follow AKM datasheet options and Aurender published material. Dynamic range, THD+N, channel separation, output impedance, jitter and idle-tone level are ESTIMATED from the product class rather than quoted, and the output-stage classification is inferred.',
  }),
];
